// src/index.tsx
import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css";

// ==============================
// ENVIRONMENT DETECTION
// ==============================
const IS_PRODUCTION = process.env.NODE_ENV === "production";
const IS_DEVELOPMENT = process.env.NODE_ENV === "development";

/**
 * CONSOLE LOG CONTROL SYSTEM
 *
 * In production, all console methods are disabled by default.
 * In development, they can be selectively enabled for debugging.
 *
 * To enable logs in production (emergency debugging only):
 * 1. Set ENABLE_EMERGENCY_LOGS = true
 * 2. Use window.enableConsole() in browser console
 */
const CONSOLE_CONTROL = {
  // Master switches - all false in production by default
  ENABLE_CONSOLE_LOGS: !IS_PRODUCTION, // false in prod, true in dev
  ENABLE_CONSOLE_WARN: !IS_PRODUCTION, // false in prod, true in dev
  ENABLE_CONSOLE_ERROR: !IS_PRODUCTION, // false in prod, true in dev
  ENABLE_CONSOLE_INFO: !IS_PRODUCTION, // false in prod, true in dev
  ENABLE_CONSOLE_DEBUG: !IS_PRODUCTION, // false in prod, true in dev

  // Emergency override (use with caution!)
  ENABLE_EMERGENCY_LOGS: false,

  // Whitelist specific log messages (these will show even if logs are disabled)
  WHITELISTED_PATTERNS: [
    /^\[LRNSY\]/i, // Our own branded logs
    /^🚨/i, // Emergency alerts
    /^✅/i, // Success indicators
    /^❌/i, // Error indicators
    /^⚠️/i, // Warning indicators
    /^💰/i, // Payment-related
    /^🔐/i, // Auth-related
    /^church/i, // Church-related
    /^critical/i, // Critical errors
  ],

  // Blacklist specific log messages (blocked even if logs enabled)
  BLACKLISTED_PATTERNS: [
    /supabase/i, // Hide supabase internal logs
    /deprecated/i, // Hide deprecation warnings
    /redux/i, // Hide redux logs
    /react-i18next/i, // Hide i18n logs
    /\[HMR\]/i, // Hide HMR logs
    /\[webpack-dev-server\]/i, // Hide webpack logs
  ],

  // Performance monitoring
  LOG_PERFORMANCE: !IS_PRODUCTION,

  // Track suppressed logs (dev only)
  TRACK_SUPPRESSED: IS_DEVELOPMENT,
} as const;

// Save original console methods
const originalConsole = {
  log: console.log,
  warn: console.warn,
  error: console.error,
  info: console.info,
  debug: console.debug,
  group: console.group,
  groupCollapsed: console.groupCollapsed,
  groupEnd: console.groupEnd,
};

// Suppressed log counter (for monitoring)
let suppressedCount = 0;

/**
 * Check if a log message should be displayed
 */
const shouldShowLog = (args: any[], type: keyof typeof CONSOLE_CONTROL): boolean => {
  // Emergency override
  if (CONSOLE_CONTROL.ENABLE_EMERGENCY_LOGS) {
    return true;
  }

  // Check master switch
  if (!CONSOLE_CONTROL[type]) {
    return false;
  }

  const message = args
    .map((arg) => (typeof arg === "object" ? JSON.stringify(arg) : String(arg)))
    .join(" ");

  // Check whitelist
  const isWhitelisted = CONSOLE_CONTROL.WHITELISTED_PATTERNS.some((pattern) =>
    pattern.test(message)
  );
  if (isWhitelisted) {
    return true;
  }

  // Check blacklist
  const isBlacklisted = CONSOLE_CONTROL.BLACKLISTED_PATTERNS.some((pattern) =>
    pattern.test(message)
  );
  if (isBlacklisted) {
    if (CONSOLE_CONTROL.TRACK_SUPPRESSED) {
      suppressedCount++;
      if (suppressedCount % 100 === 0) {
        originalConsole.log(`[LRNSY] Suppressed ${suppressedCount} logs`);
      }
    }
    return false;
  }

  return true;
};

/**
 * Create a wrapped console method
 */
const createConsoleWrapper = (
  method: keyof typeof originalConsole,
  controlKey: keyof typeof CONSOLE_CONTROL
) => {
  return function (this: Console, ...args: any[]) {
    if (shouldShowLog(args, controlKey)) {
      // Add [LRNSY] prefix to our logs for easy filtering
      if (
        controlKey === "ENABLE_CONSOLE_LOGS" &&
        !args[0]?.startsWith?.("[LRNSY]") &&
        !args[0]?.startsWith?.("🚨") &&
        !args[0]?.startsWith?.("💰")
      ) {
        originalConsole[method].apply(this, ["[LRNSY]", ...args]);
      } else {
        originalConsole[method].apply(this, args);
      }
    }
  };
};

// Override console methods
console.log = createConsoleWrapper("log", "ENABLE_CONSOLE_LOGS");
console.warn = createConsoleWrapper("warn", "ENABLE_CONSOLE_WARN");
console.error = createConsoleWrapper("error", "ENABLE_CONSOLE_ERROR");
console.info = createConsoleWrapper("info", "ENABLE_CONSOLE_INFO");
console.debug = createConsoleWrapper("debug", "ENABLE_CONSOLE_DEBUG");

// Preserve group functionality
console.group = originalConsole.group;
console.groupCollapsed = originalConsole.groupCollapsed;
console.groupEnd = originalConsole.groupEnd;

/**
 * Emergency console control (accessible in browser)
 */
(window as any).__LRNSY_CONSOLE = {
  /**
   * Enable console logs (use with caution!)
   */
  enable: (options?: {
    logs?: boolean;
    warn?: boolean;
    error?: boolean;
    info?: boolean;
    debug?: boolean;
  }) => {
    const settings = options || {
      logs: true,
      warn: true,
      error: true,
      info: true,
      debug: true,
    };

    Object.assign(CONSOLE_CONTROL, {
      ENABLE_CONSOLE_LOGS: settings.logs ?? true,
      ENABLE_CONSOLE_WARN: settings.warn ?? true,
      ENABLE_CONSOLE_ERROR: settings.error ?? true,
      ENABLE_CONSOLE_INFO: settings.info ?? true,
      ENABLE_CONSOLE_DEBUG: settings.debug ?? true,
    });

    originalConsole.log("[LRNSY] ✅ Console logs enabled");
    originalConsole.log("[LRNSY] Settings:", settings);
  },

  /**
   * Disable console logs
   */
  disable: () => {
    Object.assign(CONSOLE_CONTROL, {
      ENABLE_CONSOLE_LOGS: false,
      ENABLE_CONSOLE_WARN: false,
      ENABLE_CONSOLE_ERROR: false,
      ENABLE_CONSOLE_INFO: false,
      ENABLE_CONSOLE_DEBUG: false,
    });
    originalConsole.log("[LRNSY] 🔇 Console logs disabled");
  },

  /**
   * Emergency override - shows everything
   */
  emergency: () => {
    (CONSOLE_CONTROL as any).ENABLE_EMERGENCY_LOGS = true;
    originalConsole.log("[LRNSY] 🚨 EMERGENCY MODE ENABLED");
  },

  /**
   * Get current status
   */
  status: () => {
    originalConsole.log("[LRNSY] Console Control Status:", {
      environment: process.env.NODE_ENV,
      logs: CONSOLE_CONTROL.ENABLE_CONSOLE_LOGS,
      warn: CONSOLE_CONTROL.ENABLE_CONSOLE_WARN,
      error: CONSOLE_CONTROL.ENABLE_CONSOLE_ERROR,
      info: CONSOLE_CONTROL.ENABLE_CONSOLE_INFO,
      debug: CONSOLE_CONTROL.ENABLE_CONSOLE_DEBUG,
      emergency: (CONSOLE_CONTROL as any).ENABLE_EMERGENCY_LOGS,
      suppressedCount,
    });
  },
};

// ==============================
// REACT APP RENDERING
// ==============================
const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// Log initialization
if (!IS_PRODUCTION) {
  console.log("[LRNSY] 🚀 Application initialized");
  console.log("[LRNSY] Environment:", process.env.NODE_ENV);
  console.log("[LRNSY] Type window.__LRNSY_CONSOLE.status() to view console settings");
} else {
  // In production, log nothing by default
  // But keep the emergency access available
  console.log = () => {}; // No-op
}
